/** Best-effort HRL parse + VARIABLE_VALUE resolution for call-center Summary view. */

const QUOTED = /"([^"]+)"|'([^']+)'/g

function allMatches(re, text) {
  const out = []
  const r = new RegExp(re.source, re.flags.includes('g') ? re.flags : `${re.flags}g`)
  let m
  while ((m = r.exec(text)) !== null) out.push(m)
  return out
}

/** Strip HRP default/value suffix: "INN Coins(50(Default))" → "INN Coins" */
function baseVariableName(name) {
  if (!name) return name
  return String(name)
    .replace(/\(\$?[\d,.]+(?:\s*\(\s*Default\s*\))?\)\s*$/i, '')
    .replace(/\([^()]*\(\s*Default\s*\)\)\s*$/i, '')
    .trim()
}

/** Pull embedded value when VARIABLE_VALUE lookup misses: "…($1,600.00(Default))" → "$1,600.00" */
function embeddedDisplay(name) {
  if (!name) return null
  const m = String(name).match(/\((\$?[\d,.]+)(?:\s*\(\s*Default\s*\))?\)\s*$/i)
  return m?.[1] || null
}

function resolveName(name, variableValues) {
  if (!name) return null
  const direct = variableValues?.[name]
  if (direct) return { name, display: direct.display, resolved: true, ...direct }

  const base = baseVariableName(name)
  if (base && base !== name) {
    const hit = variableValues?.[base]
    if (hit) return { name: base, display: hit.display, resolved: true, rawName: name, ...hit }
  }

  const embedded = embeddedDisplay(name)
  if (embedded) {
    const looksMoney = embedded.startsWith('$') || /deductible|oop|copay|reimburse|dollar/i.test(name)
    const looksPct = /coins|percent|%/i.test(name)
    let display = embedded
    if (looksPct && !/%$/.test(embedded) && !embedded.startsWith('$')) display = `${embedded}%`
    else if (looksMoney && !embedded.startsWith('$') && !/%$/.test(embedded)) {
      const n = Number(String(embedded).replace(/,/g, ''))
      if (Number.isFinite(n)) {
        display = `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`
      }
    }
    return { name: base || name, display, resolved: true, embedded: true, rawName: name }
  }

  return { name, display: null, resolved: false }
}

function formatResolved(resolved) {
  if (!resolved) return null
  if (resolved.display && resolved.resolved) return resolved.display
  return resolved.name
}

function formatResolvedLabeled(resolved) {
  if (!resolved) return null
  if (resolved.display && resolved.resolved) return `${resolved.name} · ${resolved.display}`
  return resolved.name
}

/** Extract service categories, including shorthand lists: service category "A", "B", "C" */
function catsIn(block) {
  if (!block) return []
  const set = new Set()
  const re = /service\s+category\s+(?:"([^"]+)"|'([^']+)')((?:\s*,\s*(?:"([^"]+)"|'([^']+)'))*)/gi
  let m
  while ((m = re.exec(block)) !== null) {
    if (m[1] || m[2]) set.add(m[1] || m[2])
    if (m[3]) {
      for (const q of m[3].matchAll(/"([^"]+)"|'([^']+)'/g)) {
        if (q[1] || q[2]) set.add(q[1] || q[2])
      }
    }
  }
  return [...set]
}

/** CPT / HCPCS / service codes: benefit for service "99219" (not "service category") */
function servicesIn(block) {
  if (!block) return []
  return allMatches(/(?<!category\s)service\s+(?:"([^"]+)"|'([^']+)')/gi, block)
    .map((m) => m[1] || m[2])
    .filter(Boolean)
}

function amountToken(quoted, literal) {
  if (quoted) return quoted
  if (literal) return literal
  return null
}

function joinAnd(list) {
  if (!list.length) return ''
  if (list.length === 1) return list[0]
  return list.join(' + ')
}

function titleCasePeriod(raw) {
  if (!raw) return null
  return String(raw).replace(/\b\w/g, (c) => c.toUpperCase())
}

function extractMessage(text) {
  return (
    text.match(/use\s+review\s+message\s+"([^"]+)"/i)?.[1]
    || text.match(/use\s+message\s+"([^"]+)"/i)?.[1]
    || null
  )
}

/**
 * @param {{ type?: string, text?: string }} provision
 * @param {Record<string, { display?: string|null }>} [variableValues]
 */
export function parseProvision(provision, variableValues = {}) {
  const text = provision?.text || ''
  const kind = provision?.type || null

  // —— exclusions / not-blocks ——
  let excludes = []
  const notBlock = text.match(/and not\s*\(([^)]*)\)/i)
  const notInner = notBlock?.[1] || ''
  if (notBlock) {
    excludes = catsIn(notInner)
    const netsInNot = allMatches(/supplier network\s+"([^"]+)"/gi, notInner).map((m) => m[1])
    excludes = [...excludes, ...netsInNot.map((n) => `network ${n}`)]
  }
  excludes = [
    ...excludes,
    ...allMatches(/and\s+not\s+service\s+category\s+"([^"]+)"/gi, text).map((m) => m[1]),
  ]

  // exclusion for not ( ... ) → conditions that avoid the exclusion
  const exclusionUnlessInner = text.match(/exclusion\s+for\s+not\s*\(([^)]*)\)/i)?.[1] || ''
  const unlessConditions = []
  if (exclusionUnlessInner) {
    unlessConditions.push(
      ...allMatches(/repricer\s+"([^"]+)"/gi, exclusionUnlessInner).map((m) => `repricer ${m[1]}`),
      ...catsIn(exclusionUnlessInner).map((c) => `category ${c}`),
      ...allMatches(/claim\s+source\s+"([^"]+)"/gi, exclusionUnlessInner).map((m) => `claim source ${m[1]}`),
    )
    if (/claim\s+submitted\s+by\s+member/i.test(exclusionUnlessInner)) {
      unlessConditions.push('claim submitted by member')
    }
  }

  const excludeCatSet = new Set(excludes.filter((e) => !String(e).startsWith('network ')))

  // —— delivery / co-occurrence ——
  const sameClaimAs = catsIn(
    text.match(/delivered\s+on\s+the\s+same\s+claim\s+as\s*\(([^)]*)\)/i)?.[1]
  )
  const sameConfinementAs = catsIn(
    text.match(/delivered\s+in\s+the\s+same\s+confinement\s+as\s*\(([^)]*)\)/i)?.[1]
  )
  const sameDayAs = catsIn(
    text.match(/delivered\s+on\s+the\s+same\s+day\s+as\s*\(([^)]*)\)/i)?.[1]
  )

  // —— primary category scope ——
  const beforeDelivered = text.split(/,\s*delivered\s+/i)[0] || text
  // Strip "exclusion for not (...)" so we don't treat unless-cats as applies-to
  const scopeText = beforeDelivered
    .replace(/exclusion\s+for\s+not\s*\([^)]*\)/i, '')
    .replace(/and not\s*\([^)]*\)/i, '')

  const forParen = scopeText.match(
    /(?:for\s+greater\s+than\s+\d+\s+\w+\s+)?for\s+\(([^)]*)\)/i
  )
  let categories = forParen ? catsIn(forParen[1]) : catsIn(scopeText)
  categories = categories.filter((c) => !excludeCatSet.has(c))
  if (!categories.length) {
    categories = catsIn(text).filter((c) => !excludeCatSet.has(c))
  }

  const netsInNotSet = new Set(
    allMatches(/supplier network\s+"([^"]+)"/gi, notInner).map((m) => m[1])
  )
  const networks = allMatches(/supplier network\s+"([^"]+)"/gi, text)
    .map((m) => m[1])
    .filter((n) => !netsInNotSet.has(n))

  const periodMatch = text.match(
    /\bper\s+(plan\s+year|calendar\s+year|lifetime|claim|day|confinement)\b/i
  )
  const period = titleCasePeriod(periodMatch?.[1])

  // —— benefit / coins ——
  let benefitPercent = null
  const coinsMatch = text.match(/is\s+"([^"]+)"\s*%\s*of\s+service\s+cost/i)
  // Literal % — e.g. "benefit is 100%…" or "benefit for any service is 100 % of service cost"
  const litCoinsMatch = text.match(/is\s+(\d+(?:\.\d+)?)\s*%\s*of\s+service\s+cost/i)
  if (coinsMatch) {
    const resolved = resolveName(coinsMatch[1], variableValues)
    benefitPercent = {
      name: coinsMatch[1],
      display: resolved.display,
      text: resolved.display && resolved.resolved
        ? `${resolved.display} of service cost`
        : `${coinsMatch[1]} % of service cost`,
    }
  } else if (litCoinsMatch) {
    benefitPercent = {
      name: null,
      display: `${litCoinsMatch[1]}%`,
      text: `${litCoinsMatch[1]}% of service cost`,
    }
  }

  // —— copay (quoted variable or literal $20) ——
  let copay = null
  const copayMatch = text.match(/copay\s+of\s+(?:"([^"]+)"|'([^']+)')(?:\s+(per\s+\w+))?/i)
  const litCopayMatch = !copayMatch && text.match(/copay\s+of\s+\$?([\d,.]+)(?:\s+(per\s+\w+))?/i)
  if (copayMatch || litCopayMatch) {
    const raw = copayMatch ? (copayMatch[1] || copayMatch[2]) : `$${litCopayMatch[1]}`
    const unit = (copayMatch ? copayMatch[3] : litCopayMatch[2] || '').trim()
    const resolved = resolveName(raw, variableValues)
    const display = resolved.display || (litCopayMatch ? `$${litCopayMatch[1]}` : null)
    copay = {
      name: copayMatch ? raw : null,
      display,
      unit: unit || null,
      text: [display || formatResolved(resolved) || raw, unit].filter(Boolean).join(' '),
    }
  }

  // —— then-benefit (until member pays/receives … then benefit is X) ——
  let thenBenefit = null
  const thenMatch = text.match(
    /until\s+member\s+(receives|pays)\s+(.+?)\s+then\s+benefit\s+is\s+(?:"([^"]+)"|(\d+(?:\.\d+)?)\s*%)\s*%?\s*of\s+service\s+cost(?:\s+with\s+a\s+copay\s+of\s+(?:"([^"]+)"|\$?([\d,.]+))(?:\s+(per\s+\w+))?)?/i
  )
  if (thenMatch) {
    const thenPct = thenMatch[3]
      ? resolveName(thenMatch[3], variableValues)
      : { display: `${thenMatch[4]}%`, resolved: true }
    const thenCopayRaw = thenMatch[5] || (thenMatch[6] ? `$${thenMatch[6]}` : null)
    const thenCopay = thenCopayRaw ? resolveName(thenCopayRaw, variableValues) : null
    thenBenefit = {
      until: `${thenMatch[1]} ${thenMatch[2].replace(/\s+/g, ' ').trim()}`,
      percent: thenPct.display || thenMatch[3] || `${thenMatch[4]}%`,
      copay: thenCopay ? (thenCopay.display || thenCopayRaw) : null,
      text: [
        `Then ${thenPct.display || thenMatch[3] || `${thenMatch[4]}%`}`,
        thenCopay ? `${thenCopay.display || thenCopayRaw} copay` : null,
      ].filter(Boolean).join(' · '),
    }
  }

  // —— limits: quoted "X" units, typed individual/family limit, or literal $ / count ——
  let limit = null
  const typedLimitMatch = text.match(
    /(individual|family)\s+limit\s+for\s+(.+?)\s+is\s+(?:"([^"]+)"|(\$?[\d,.]+))\s+(units?|days|visits|reimbursement)(?:\s+per\s+([^:]+?))?(?=\s+price|\s*:|\s*$)/i
  )
  const limitMatch = !typedLimitMatch && text.match(
    /is\s+"([^"]+)"\s+(days|visits|units?|reimbursement)(?:\s+per\s+([^:]+?))?(?=\s*:|\s*$)/i
  )
  if ((typedLimitMatch || (limitMatch && !coinsMatch))) {
    const raw = typedLimitMatch
      ? amountToken(typedLimitMatch[3], typedLimitMatch[4])
      : limitMatch[1]
    const unit = typedLimitMatch ? typedLimitMatch[5] : limitMatch[2]
    const limitPeriod = (typedLimitMatch ? typedLimitMatch[6] : limitMatch[3] || '').trim()
    const resolved = resolveName(raw, variableValues)
    const display = resolved.display || (/^\$/.test(raw) ? raw : null)
    const unitLabel = unit.toLowerCase() === 'reimbursement' ? 'reimbursement' : unit
    const level = typedLimitMatch ? String(typedLimitMatch[1]).toLowerCase() : null
    limit = {
      name: typedLimitMatch?.[3] || limitMatch?.[1] || raw,
      display,
      unit: unitLabel,
      period: limitPeriod || null,
      level,
      text: [level, display || formatResolved(resolved) || raw, unitLabel, limitPeriod ? `/ ${limitPeriod}` : null]
        .filter(Boolean)
        .join(' ')
        .replace(/\s+\/\s+/, ' / '),
    }
  }

  // —— unit caps e.g. "up to 1 unit per lifetime" ——
  let unitCap = null
  const unitCapMatch = text.match(
    /up\s+to\s+(\d+)\s+(units?|days|visits)(?:\s+per\s+(\w+(?:\s+\w+)?))?/i
  )
  if (unitCapMatch) {
    unitCap = {
      text: [
        `Up to ${unitCapMatch[1]} ${unitCapMatch[2]}`,
        unitCapMatch[3] ? `/ ${unitCapMatch[3]}` : null,
      ]
        .filter(Boolean)
        .join(' '),
    }
  }

  // —— deductible / OOP maximum (quoted variable or literal $1,000.00) ——
  let accum = null
  const dedMatch = text.match(
    /(individual|family)\s+deductible\s+for\s+(.+?)\s+is\s+(?:"([^"]+)"|(\$?[\d,.]+))/i
  )
  const oopMatch = text.match(
    /(individual|family)\s+out\s+of\s+pocket\s+maximum\s+for\s+(.+?)\s+is\s+(?:"([^"]+)"|(\$?[\d,.]+))/i
  )
  if (dedMatch || oopMatch) {
    const m = dedMatch || oopMatch
    const scopeRaw = m[2] || ''
    const varName = amountToken(m[3], m[4])
    const resolved = resolveName(varName, variableValues)
    if (!resolved.display && m[4]) resolved.display = m[4].startsWith('$') ? m[4] : `$${m[4]}`
    const scopeService = /any service/i.test(scopeRaw)
      ? 'Any service'
      : (scopeRaw.match(/service\s+"([^"]+)"/i)?.[1]
        || scopeRaw.match(/service\s+category\s+"([^"]+)"/i)?.[1]
        || scopeRaw.replace(/\s+/g, ' ').trim())
    const including = text.match(/including\s+([^:]+?)(?=\s*:|\s*$)/i)?.[1]?.trim() || null
    const isRx = /RXACCUM/i.test(scopeRaw)
      || /\bRX\b/i.test(varName)
      || /\bRX\b/i.test(resolved.name || '')
    accum = {
      kind: dedMatch ? 'Deductible' : 'Out-of-pocket max',
      level: String(m[1]).toLowerCase(),
      appliesTo: scopeService,
      name: varName,
      display: resolved.display,
      including,
      isRx,
      text: [
        `${m[1]} ${dedMatch ? 'deductible' : 'OOP max'}`,
        formatResolved(resolved),
      ]
        .filter(Boolean)
        .join(' · '),
    }
  }

  // —— labels ——
  let label = null
  const labelMatch =
    text.match(/benefit\s+label\s+"([^"]+)"/i) || text.match(/:\s*label\s+"([^"]+)"/i)
  if (labelMatch) label = labelMatch[1]
  const alternateLabels = allMatches(/:\s*alternate\s+label\s+"([^"]+)"/gi, text).map((m) => m[1])

  // —— conditions ——
  const deductibleWaived = /deductible\s+waived/i.test(text)
  const requiresReview = /requires\s+review/i.test(text)
  const renderedByPcp = /rendered\s+by\s+member'?s?\s+PCP/i.test(text)
  const samePractitioner = /(?:for|and)\s+the\s+same\s+practitioner/i.test(text)
  const sameSupplier = /(?:for|and)\s+the\s+same\s+supplier/i.test(text)
  const isExclusionOverride = /exclusion\s+override/i.test(text) || /ExclusionOverride/i.test(kind || '')
  const isExclusion = !isExclusionOverride && (
    /^exclusion\b/i.test(text.trim()) || (/Exclusion/i.test(kind || '') && !/Override/i.test(kind || ''))
  )
  const authWaived = /service\s+authorization\s+requirement\s+waived/i.test(text)
  const referralRequired = /referral\s+is\s+required/i.test(text)
  const forAnyService = /for\s+any\s+service\b/i.test(text)
  const isDefault = /DefaultBenefit/i.test(kind || '') || (forAnyService && !isExclusion && !isExclusionOverride)
  const sameDiagnosis = /same\s+diagnosis/i.test(text)
  const carveOut = text.match(/carve\s+out\s+vendor\s+"([^"]+)"/i)?.[1] || null
  const paidAgainst = text.match(/paid\s+against\s+a\s+"([^"]+)"\s+benefit/i)?.[1] || null
  const benefitCalcTier = text.match(/benefit\s+calculation\s+using\s+(.+?)\s+tier/i)?.[1]?.trim() || null
  const billedOn = allMatches(/billed\s+on\s+(professional|institutional|dental)\s+claims/gi, text)
    .map((m) => m[1])
  const afterMembers = text.match(/only\s+applies\s+after\s+(\d+)\s+members/i)?.[1] || null
  const waivedIf = text.match(/is\s+waived\s+if\s+(.+?)(?=\s+also\s+include|\s*:|\s*$)/i)?.[1]?.trim() || null
  const alsoInclude = text.match(/also\s+include\s+(.+?)(?=\s*:|\s*$)/i)?.[1]?.trim() || null
  const serviceCodes = servicesIn(scopeText.length ? scopeText : text)

  const memberAges = allMatches(
    /member\s+age\s*(>=|<=|>|<|=)\s*(\d+)\s*years?/gi,
    text
  ).map((m) => `${m[1]} ${m[2]}`)
  const memberAgeText = memberAges.length
    ? memberAges.join(' and ')
    : null

  const memberRelationships = allMatches(
    /member\s+relationship\s+"([^"]+)"/gi,
    text
  ).map((m) => m[1])

  const claimSources = allMatches(/claim\s+source\s+"([^"]+)"/gi, text).map((m) => m[1])
  const repricers = allMatches(/repricer\s+"([^"]+)"/gi, text).map((m) => m[1])
  const message = extractMessage(text)
  const messageCode = text.match(/user\s+message\s+code\s+"([^"]+)"/i)?.[1]
    || text.match(/use\s+message\s+code\s+"([^"]+)"/i)?.[1]
    || null

  if (forAnyService && !categories.length) categories.push('Any service')

  // —— authorization / UM ——
  let authorization = null
  const authRequired = /authorization\s+is\s+required/i.test(text)
  if (authRequired || authWaived || referralRequired || /UtilizationManagement|ExemptionItem/i.test(kind || '')) {
    const typeMatch = text.match(/specific\s+type\s+"([^"]+)"/i)
    const thresholdMatch = text.match(
      /for\s+greater\s+than\s+(\d+)\s+(days|visits|units?|hours?)/i
    )
    const penaltyNoReimb = /penalty\s+applied\s+is\s+no\s+reimbursement/i.test(text)
    const penaltyVarMatch = text.match(
      /penalty\s+applied\s+is\s+(?:"([^"]+)"|(\d+(?:\.\d+)?)\s*%(?:\s+(increase\s+in\s+copay|reduction\s+in\s+benefit))?)(?:\s+(per\s+\w+))?/i
    )
    const penaltyCapMatch = text.match(
      /with\s+a?\s*cap\s+of\s+(?:"([^"]+)"|'([^']+)'|(\$?[\d,.]+))\s+per\s+(\w+(?:\s+\w+)?)/i
    )

    let penalty = null
    if (penaltyNoReimb) {
      penalty = { text: 'No reimbursement', name: null, display: null }
    } else if (penaltyVarMatch) {
      const raw = penaltyVarMatch[1] || `${penaltyVarMatch[2]}%`
      const resolved = resolveName(raw, variableValues)
      const kind = penaltyVarMatch[3] || (/reduction\s+in\s+benefit/i.test(text) ? 'reduction in benefit' : null)
      const unit = (penaltyVarMatch[4] || '').trim()
      const capRaw = penaltyCapMatch
        ? (penaltyCapMatch[1] || penaltyCapMatch[2] || penaltyCapMatch[3])
        : null
      const capResolved = capRaw ? resolveName(capRaw, variableValues) : null
      const capPeriod = penaltyCapMatch?.[4] || null
      penalty = {
        name: penaltyVarMatch[1] || null,
        display: resolved.display || (penaltyVarMatch[2] ? `${penaltyVarMatch[2]}%` : null),
        cap: capResolved ? (capResolved.display || capRaw) : null,
        capPeriod,
        text: [
          resolved.display || raw,
          kind,
          unit,
          capResolved || capRaw
            ? `cap ${capResolved?.display || capRaw}${capPeriod ? ` / ${capPeriod}` : ''}`
            : null,
        ]
          .filter(Boolean)
          .join(' '),
      }
    }

    const threshold = thresholdMatch
      ? `Greater than ${thresholdMatch[1]} ${thresholdMatch[2]}`
      : null

    authorization = {
      required: authRequired,
      waived: authWaived,
      type: typeMatch?.[1] || null,
      threshold,
      period,
      penalty,
      message: message || null,
      messageCode,
      sameClaimAs,
      sameConfinementAs,
      sameDayAs,
      appliesText: joinAnd(categories),
      referralRequired,
      text: authWaived
        ? 'Waived'
        : authRequired
          ? 'Required'
          : referralRequired
            ? 'Referral required'
            : 'UM rule',
    }
  }

  // —— quoted variable leftovers ——
  const used = new Set([
    ...categories,
    ...sameClaimAs,
    ...sameConfinementAs,
    ...sameDayAs,
    ...excludes.filter((e) => !String(e).startsWith('network ')),
    ...networks,
    ...memberRelationships,
    ...claimSources,
    ...repricers,
    ...alternateLabels,
    coinsMatch?.[1],
    copayMatch?.[1],
    typedLimitMatch?.[3],
    limitMatch?.[1],
    accum?.name,
    label,
    message,
    messageCode,
    authorization?.type,
    authorization?.penalty?.name,
    carveOut,
    paidAgainst,
    ...serviceCodes,
  ].filter(Boolean))

  const unresolved = []
  let m
  const qre = new RegExp(QUOTED.source, 'g')
  while ((m = qre.exec(text)) !== null) {
    const name = m[1] || m[2]
    if (!name || used.has(name)) continue
    const resolved = resolveName(name, variableValues)
    if (resolved.resolved) {
      unresolved.push(formatResolvedLabeled(resolved))
      used.add(name)
    }
  }

  const fields = [
    benefitPercent,
    copay,
    limit,
    unitCap,
    accum,
    label,
    categories.length,
    excludes.length,
    unlessConditions.length,
    deductibleWaived,
    requiresReview,
    authorization,
    isExclusion,
    isExclusionOverride,
    isDefault,
    thenBenefit,
    carveOut,
    paidAgainst,
    benefitCalcTier,
    serviceCodes.length,
    referralRequired,
    memberAgeText,
    memberRelationships.length,
    claimSources.length,
    repricers.length,
    message && !authorization,
  ].filter(Boolean)

  const hasStructure = fields.length > 0

  return {
    kind,
    label,
    alternateLabels,
    benefitPercent,
    copay,
    limit,
    unitCap,
    accum,
    categories: [...new Set(categories)],
    excludes: [...new Set(excludes)],
    unlessConditions: [...new Set(unlessConditions)],
    networks: [...new Set(networks)],
    sameClaimAs,
    sameConfinementAs,
    sameDayAs,
    period,
    deductibleWaived,
    requiresReview,
    renderedByPcp,
    samePractitioner,
    sameSupplier,
    isExclusion,
    isExclusionOverride,
    isDefault,
    forAnyService,
    thenBenefit,
    carveOut,
    paidAgainst,
    benefitCalcTier,
    billedOn,
    afterMembers,
    waivedIf,
    alsoInclude,
    serviceCodes: [...new Set(serviceCodes)],
    sameDiagnosis,
    referralRequired,
    memberAgeText,
    memberRelationships,
    claimSources,
    repricers,
    message: authorization ? null : message,
    messageCode: authorization ? null : messageCode,
    authorization,
    unresolved,
    hasStructure,
    populatedText: populateText(text, variableValues),
  }
}

/** Replace quoted VALUE_NAME tokens with "Name (display)" when resolved. */
export function populateText(text, variableValues = {}) {
  if (!text) return text
  return text.replace(QUOTED, (full, dquote, squote) => {
    const name = dquote || squote
    const resolved = resolveName(name, variableValues)
    if (!resolved?.resolved || !resolved.display) return full
    if (/\(\$[\d,]/.test(name) || /\(\d+%/.test(name)) return full
    const label = resolved.name || name
    return `"${label} (${resolved.display})"`
  })
}
